import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Checklist,
  Refresh,
  SystemUpdate,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import DeviceCard from './DeviceCard';
import DeviceNameEditor from './DeviceNameEditor';
import { DataTable, DataTableColumn } from '../table';
import { Device, displayName, isDeviceOnline } from '../types';
import { deviceApi, taskApi } from '../api';
import { deviceOnLatestFirmware, lastJobCommandLabel, lastJobSortValue } from '../devices/lastJob';
import { ChannelChip, deviceTransport } from '../devices/ChannelChip';
import { productLabel } from '../flash/profiles';
import { formatRelativeTime, formatTime, useI18n } from '../i18n';
import { controllerDetailPath } from '../nav/paths';

const VIEW_STORAGE_KEY = 'vcs-device-list-layout';

type DeviceView = 'cards' | 'table';

function jobStateColor(state: string | undefined): 'warning' | 'info' | 'success' | 'error' | 'default' {
  switch (state) {
    case 'pending':
      return 'warning';
    case 'running':
      return 'info';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

const DeviceList: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<string | null>(null);
  const [view, setView] = useState<DeviceView>(() => {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY);
      return stored === 'cards' ? 'cards' : 'table';
    } catch {
      return 'table';
    }
  });

  const loadDevices = async () => {
    try {
      setError(null);
      const deviceList = await deviceApi.getDevices();
      setDevices(deviceList);
      setSelectedIds((prev) => prev.filter((id) => deviceList.some((d) => d.id === id)));
      setLastUpdate(new Date());
    } catch (err) {
      setError(t('devices.loadFailed'));
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const hasActiveJobs = devices.some((d) => d.lastTask?.state === 'pending' || d.lastTask?.state === 'running');

  useEffect(() => {
    loadDevices();
  }, []);

  useEffect(() => {
    const interval = setInterval(loadDevices, hasActiveJobs ? 5000 : 30000);
    return () => clearInterval(interval);
  }, [hasActiveJobs]);

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, next: DeviceView | null) => {
    if (!next) return;
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore quota / private mode
    }
  };

  const handleDeviceUpdated = (updated: Device) => {
    setDevices((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
  };

  const selectableDevices = useMemo(() => devices.filter((d) => Boolean(d.id)), [devices]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedDevices = useMemo(
    () => selectableDevices.filter((d) => d.id && selectedIdSet.has(d.id)),
    [selectableDevices, selectedIdSet],
  );
  const allSelected = selectableDevices.length > 0 && selectedDevices.length === selectableDevices.length;
  const onlineDevices = devices.filter(isDeviceOnline);
  const offlineDevices = devices.filter((d) => !isDeviceOnline(d));

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return Array.from(next);
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(selectableDevices.map((d) => d.id as string));
  };

  const handleSelectNone = () => {
    setSelectedIds([]);
  };

  const handleBulkUpdate = async () => {
    const targets = selectedDevices.filter((d) => d.id && d.latestFirmware?.filename);
    if (targets.length === 0) {
      return;
    }
    setUpdating(true);
    setUpdateResult(null);
    let ok = 0;
    let failed = 0;
    for (const device of targets) {
      try {
        await taskApi.queueOta(device.id as string);
        ok += 1;
      } catch (err) {
        failed += 1;
        console.error('OTA queue failed', device.id, err);
      }
    }
    setUpdating(false);
    setConfirmOpen(false);
    setUpdateResult(t('devices.updateLatestQueued', { ok, failed }));
    await loadDevices();
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, px: { xs: 2, md: 3 }, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('devices.loading')}
        </Typography>
      </Container>
    );
  }

  const renderCardSection = (title: string, list: Device[]) => {
    if (list.length === 0) return null;
    return (
      <>
        <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
          {title}
        </Typography>
        <Grid container spacing={3}>
          {list.map((device, index) => (
            <Grid item xs={12} sm={6} md={4} key={device.id || index}>
              <DeviceCard
                device={device}
                onUpdated={handleDeviceUpdated}
                selected={Boolean(device.id && selectedIdSet.has(device.id))}
                onSelectedChange={device.id ? (checked) => toggleSelected(device.id as string, checked) : undefined}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  };

  const renderTable = () => {
    const columns: DataTableColumn<Device>[] = [
      {
        id: 'name',
        label: t('common.name'),
        accessor: (d) => displayName(d),
        stopRowClick: true,
        render: (d) => (
          <Box sx={{ minWidth: 200 }}>
            <DeviceNameEditor device={d} variant="body1" onUpdated={handleDeviceUpdated} />
          </Box>
        ),
      },
      {
        id: 'hostname',
        label: t('common.hostname'),
        accessor: (d) => d.hostname || '',
      },
      {
        id: 'status',
        label: t('common.status'),
        accessor: (d) => (isDeviceOnline(d) ? t('common.online') : t('common.offline')),
        filter: 'select',
        filterOptions: [
          { value: t('common.online'), label: t('common.online') },
          { value: t('common.offline'), label: t('common.offline') },
        ],
        sortValue: (d) => (isDeviceOnline(d) ? 1 : 0),
        render: (d) => {
          const online = isDeviceOnline(d);
          return (
            <Chip
              label={online ? t('common.online') : t('common.offline')}
              color={online ? 'success' : 'default'}
              size="small"
            />
          );
        },
      },
      {
        id: 'model',
        label: t('common.model'),
        accessor: (d) => d.model || '',
        filter: 'select',
      },
      {
        id: 'channel',
        label: t('common.channel'),
        accessor: (d) => {
          const tr = deviceTransport(d);
          if (tr === 'wifi') return t('devices.channelWifi');
          if (tr === 'zigbee') return t('devices.channelZigbee');
          if (tr === 'cellular') return t('devices.channelCellular');
          if (tr === 'both') return t('devices.channelBoth');
          return '';
        },
        filter: 'select',
        render: (d) => <ChannelChip device={d} />,
      },
      {
        id: 'firmware',
        label: t('common.firmware'),
        accessor: (d) => d.firmware || d.version || '',
        render: (d) => (
          <Box sx={{ maxWidth: 280, wordBreak: 'break-word' }}>
            {d.firmware || d.version || t('common.emdash')}
          </Box>
        ),
      },
      {
        id: 'lastJob',
        label: t('devices.lastJob'),
        accessor: (d) => (d.lastTask ? t(`tasks.state.${d.lastTask.state}`) : t('devices.lastJobNone')),
        filter: 'select',
        filterOptions: [
          { value: t('devices.lastJobNone'), label: t('devices.lastJobNone') },
          { value: t('tasks.state.pending'), label: t('tasks.state.pending') },
          { value: t('tasks.state.running'), label: t('tasks.state.running') },
          { value: t('tasks.state.completed'), label: t('tasks.state.completed') },
          { value: t('tasks.state.failed'), label: t('tasks.state.failed') },
          { value: t('tasks.state.cancelled'), label: t('tasks.state.cancelled') },
        ],
        sortValue: (d) => lastJobSortValue(d.lastTask),
        render: (d) => {
          const task = d.lastTask;
          if (!task) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t('devices.lastJobNone')}
              </Typography>
            );
          }
          return (
            <Box>
              <Tooltip
                title={[formatRelativeTime(task.updatedAt), task.result].filter(Boolean).join(' — ') || t(`tasks.state.${task.state}`)}
              >
                <Chip
                  label={t(`tasks.state.${task.state}`)}
                  color={jobStateColor(task.state)}
                  size="small"
                  sx={{ mr: 0.75 }}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary" component="span">
                {lastJobCommandLabel(task.command)}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: 'ip',
        label: t('common.ip'),
        accessor: (d) => d.ip || '',
      },
      {
        id: 'rssi',
        label: t('common.rssi'),
        accessor: (d) => (d.rssi ? t('detail.rssiDbm', { rssi: d.rssi }) : ''),
        sortValue: (d) => (Number.isFinite(d.rssi) ? d.rssi : null),
        align: 'right',
      },
      {
        id: 'fileDrift',
        label: t('devices.fileDrift'),
        accessor: (d) => {
          const drift = d.fileDrift;
          if (!drift?.reported) return t('devices.fileDriftUnknown');
          if (drift.clean) return t('devices.fileDriftClean');
          return t('devices.fileDriftCount', { count: drift.changed });
        },
        filter: 'select',
        sortValue: (d) => (d.fileDrift?.reported ? d.fileDrift.changed : -1),
        render: (d) => {
          const drift = d.fileDrift;
          const unknown = !drift?.reported;
          const clean = Boolean(drift?.reported && drift.clean);
          const label = unknown
            ? t('devices.fileDriftUnknown')
            : clean
              ? t('devices.fileDriftClean')
              : t('devices.fileDriftCount', { count: drift?.changed || 0 });
          const tip = (drift?.files || [])
            .map((row) => `${row.path} — ${t(`files.syncState.${row.status}`)}`)
            .join('\n');
          return (
            <Tooltip title={tip || label}>
              <Chip
                label={label}
                size="small"
                color={unknown ? 'default' : clean ? 'success' : 'warning'}
              />
            </Tooltip>
          );
        },
      },
      {
        id: 'lastSeen',
        label: t('common.lastSeen'),
        accessor: (d) => formatRelativeTime(d.lastSeen),
        sortValue: (d) => d.lastSeen || 0,
      },
    ];

    return (
      <Box sx={{ mt: 2 }}>
        <DataTable
          rows={devices}
          columns={columns}
          getRowId={(d) => d.id || displayName(d)}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          defaultSort={{ id: 'lastSeen', dir: 'desc' }}
          onRowClick={(d) => {
            if (d.id) {
              navigate(controllerDetailPath(d.id));
            }
          }}
          emptyMessage={t('devices.emptyFiltered')}
          maxHeight="calc(100vh - 280px)"
        />
      </Box>
    );
  };

  const updateTargets = selectedDevices.filter((d) => d.latestFirmware?.filename);
  const updateSkipped = selectedDevices.filter((d) => !d.latestFirmware?.filename);

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, md: 3 } }}>
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" component="h1">
              {t('devices.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('devices.subtitle')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip label={t('devices.onlineCount', { count: onlineDevices.length })} color="success" size="small" />
            <Chip label={t('devices.offlineCount', { count: offlineDevices.length })} size="small" />
            <Chip
              label={t('time.lastUpdated', { time: formatTime(lastUpdate) })}
              variant="outlined"
              size="small"
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={handleViewChange}
              aria-label={t('devices.view')}
            >
              <ToggleButton value="table" aria-label={t('devices.tableView')} sx={{ gap: 0.75, px: 1.25 }}>
                <ViewList fontSize="small" />
                {t('devices.viewTable')}
              </ToggleButton>
              <ToggleButton value="cards" aria-label={t('devices.cardView')} sx={{ gap: 0.75, px: 1.25 }}>
                <ViewModule fontSize="small" />
                {t('devices.viewCards')}
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={loadDevices} color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {devices.length > 0 && (
          <Box display="flex" gap={1} mt={2} flexWrap="wrap" alignItems="center">
            {view === 'cards' && (
              <>
                <Button size="small" startIcon={<Checklist />} onClick={handleSelectAll} disabled={allSelected}>
                  {t('devices.selectAll')}
                </Button>
                <Button size="small" onClick={handleSelectNone} disabled={selectedIds.length === 0}>
                  {t('devices.selectNone')}
                </Button>
              </>
            )}
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<SystemUpdate />}
              disabled={selectedIds.length === 0}
              onClick={() => setConfirmOpen(true)}
            >
              {t('devices.updateLatest')}
            </Button>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {updateResult && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setUpdateResult(null)}>
          {updateResult}
        </Alert>
      )}

      {devices.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('devices.emptyTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('devices.emptyBody')}
          </Typography>
        </Paper>
      ) : view === 'table' ? (
        renderTable()
      ) : (
        <>
          {renderCardSection(t('devices.onlineSection', { count: onlineDevices.length }), onlineDevices)}
          {renderCardSection(t('devices.offlineSection', { count: offlineDevices.length }), offlineDevices)}
        </>
      )}

      <Fab
        color="primary"
        aria-label={t('common.refresh')}
        sx={{ position: 'fixed', right: 16, bottom: { xs: 88, md: 24 } }}
        onClick={loadDevices}
      >
        <Refresh />
      </Fab>

      <Dialog open={confirmOpen} onClose={() => !updating && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('devices.updateLatestConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{t('devices.updateLatestConfirmBody')}</Typography>
          <Alert severity="warning" sx={{ my: 2 }}>
            {t('firmware.warningBody')}
          </Alert>
          {updateTargets.length === 0 ? (
            <Typography color="text.secondary">{t('devices.updateLatestNone')}</Typography>
          ) : (
            <List dense>
              {updateTargets.map((device) => {
                const fw = device.latestFirmware;
                const already = deviceOnLatestFirmware(device);
                const target = fw
                  ? t('devices.updateLatestTarget', {
                      product: productLabel(fw.environment, t),
                      file: fw.versionString || fw.filename,
                    })
                  : t('devices.updateLatestNoImage');
                return (
                  <ListItem key={device.id} disableGutters>
                    <ListItemText
                      primary={displayName(device)}
                      secondary={already ? `${target} — ${t('devices.updateLatestAlready')}` : target}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          {updateSkipped.length > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('devices.updateLatestSkipped', { count: updateSkipped.length })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={updating}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleBulkUpdate}
            variant="contained"
            color="warning"
            disabled={updating || updateTargets.length === 0}
            startIcon={updating ? <CircularProgress size={16} /> : <SystemUpdate />}
          >
            {updating ? t('firmware.creating') : t('firmware.startUpdate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeviceList;
